import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function Gallery({ images }) {
  const cats = ["All", ...new Set((images || []).map((i) => i.category).filter(Boolean))];
  const [active, setActive] = useState("All");
  const shown = active === "All" ? images : images.filter((i) => i.category === active);

  return (
    <section id="projects" className="relative py-24 sm:py-32 bg-storm-800/20" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12"
        >
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold mb-4">Recent work</p>
            <h2 className="font-display font-bold italic uppercase text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]" data-testid="gallery-heading">
              Built to <span className="gold-text">outlast.</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`font-mono text-[11px] uppercase tracking-[0.15em] rounded-full px-4 py-2 border transition-colors ${
                  active === c
                    ? "bg-gold text-storm border-gold font-semibold"
                    : "border-gold/25 text-slate-400 hover:text-gold hover:border-gold/50"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {shown.map((img, i) => (
              <motion.figure
                layout
                key={img.id || img.url}
                initial={{ opacity: 0, y: 30, scale: 0.97 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
                className="group relative overflow-hidden rounded-2xl border border-gold/15 bg-storm-800 aspect-[4/3]"
                data-testid={`gallery-item-${i}`}
              >
                <img
                  src={img.url}
                  alt={img.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-storm via-storm/10 to-transparent opacity-80" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 shadow-[inset_0_0_0_1px_rgba(229,193,88,0.5)] rounded-2xl" />
                <figcaption className="absolute bottom-0 inset-x-0 p-5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{img.category}</span>
                  <p className="font-display font-semibold uppercase italic text-xl text-white mt-1">{img.title}</p>
                </figcaption>
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
